
import { isEmpty, isNumber, last } from 'radash';
import { useCallback } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';
import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

import { BaseValidation } from '../types';
import { useAttributeLabelTranslator } from './useAttributeLabelTranslator';

/**
 * Finds the container attribute name and row index from a validation path.
 * The container is the attribute that directly contains the invalid row/entry (e.g. Collection, MultiDimensional, Group).
 * Path examples:
 *   ['collection', 1, 'field'] -> container 'collection', rowIndex 1
 *   ['group', 0, 'collection', 1, 'field'] -> container 'collection', rowIndex 1
 *   ['group', 0, 'group_composite', 'collection', 1, 'collection_composite', 'field'] -> container 'collection', rowIndex 1
 */
function getContainerFromPath(path: (string | number | Record<string, unknown>)[]): { containerName: string; rowIndex: number } | null {
    let lastNumericIndex = -1;
    for (let i = path.length - 1; i >= 0; i--) {
        if (isNumber(path[i])) {
            lastNumericIndex = i;
            break;
        }
    }
    if (lastNumericIndex <= 0) {
        return null;
    }
    let containerName = path[lastNumericIndex - 1];
    if (typeof containerName !== 'string') {
        return null;
    }
    // Some paths include a  "*_composite" suffix.
    // The actual container attribute is the segment before it.
    if (containerName.endsWith('_composite')) {
        const prev = lastNumericIndex - 2;
        if (prev < 0) return null;
        const prevSegment = path[prev];
        containerName = typeof prevSegment === 'string' ? prevSegment : containerName;
    }
    const rowIndex = path[lastNumericIndex] as number;
    return { containerName, rowIndex };
}

/**
 * Turns an internal validation identifier into a readable, sentence-cased label so the
 * user never sees raw keys.
 */
function humanizeValidationName(validationName: string): string {
    return validationName
        .replace(/[_.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/^\p{L}/u, (char) => char.toUpperCase());
}

const useValidationTranslator = () => {

    const { t } = useTranslation();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { dataModel } = useDataModel();

    const translateValidationLabel = useCallback((validationName: string, attributeName?: string) => {

        let attributeLabel = '';
        if (attributeName) {
            attributeLabel = translateAttributeLabel({
                name: attributeName
            } as DataModelAttribute);
        }

        const interpolationValues = { value: attributeLabel };

        const resolveLinkedKey = (value: string, depth = 0): string => {
            if (depth >= 10 || !value.startsWith('@:')) {
                return value;
            }
            const linkedKey = value.substring(2);
            const linkedResult = t(linkedKey, interpolationValues);
            // Linked key has no translation: keep the "@:" so the caller falls back to a
            // humanized label instead of surfacing the raw alias to the user.
            if (linkedResult === linkedKey) {
                return value;
            }
            return resolveLinkedKey(linkedResult, depth + 1);
        };

        // Prefer the new `validationRule.<name>` key (case-sensitive); fall back to the
        // legacy upper-cased key for backwards compatibility.
        const newKey = `validationRule.${validationName}`;
        const oldKey = validationName.toUpperCase();

        let result = t(newKey, interpolationValues);
        if (result === newKey) {
            result = t(oldKey, interpolationValues);
        }
        result = resolveLinkedKey(result);

        if (result === newKey || result === oldKey || result.startsWith('@:')) {
            return humanizeValidationName(validationName);
        }

        return result;

    //eslint-disable-next-line
    }, []);

    const translateValidationMessage = useCallback((validation: BaseValidation, attributeName: string) => {

        // Prefer the new `validationRule.<name>.message[.<msgId>]` key; fall back to the
        // legacy `validationmessage.<name>[.<msgId>]` key for backwards compatibility.
        let newKey = `validationRule.${validation.name}.message`;
        let oldKey = `validationmessage.${validation.name}`;
        if (validation.params?.msg_id) {
            newKey += `.${validation.params.msg_id}`;
            oldKey += `.${validation.params.msg_id}`;
        }

        const translateMessageKey = (interpolationValues?: Record<string, string | number>): string | null => {
            const newTranslation = t(newKey, interpolationValues, 'messageformat');
            if (newTranslation !== newKey) {
                return newTranslation;
            }
            const oldTranslation = t(oldKey, interpolationValues, 'messageformat');
            return oldTranslation !== oldKey ? oldTranslation : null;
        };

        // A prefix to the validation message that indicates in which exact entry the error/warning occurred can be
        // constructed for the following attribute types: MultiDimensional, Dimensional, Group, Collection and EnumSet.
        // The translation interpolcation params are extracted and added to `validation.params` and then that is used to
        // prepare the prefix translation and then the validation message translation.
        let translatedPrefix = '', translatedValidationMessage = '';
        let prefixKey = 'VALIDATION_MESSAGE_LOCATION_PREFIX';
        if (validation.params && !isEmpty(validation.path) && validation.path.length > 1) {

            const exactAttributeName = last(validation.path) as string;
            const exactAttribute = dataModel?.attribute(exactAttributeName);

            if (!exactAttribute) {
                logger.error(`Attribute ${exactAttributeName} used in validation message was not found`, validation);
            } else {
                validation.params.err_field = translateAttributeLabel(exactAttribute);
            }

            // Use attributeName first; for nested groups / multi-dim the server may
            // key validations differently, so fall back to the first path segment (root attribute in path).
            let rootAttribute = dataModel?.attribute(attributeName);
            if (!rootAttribute && validation.path.length > 0) {
                const pathRoot = validation.path[0];
                const rootName = typeof pathRoot === 'string' ? pathRoot : attributeName;
                rootAttribute = rootName ? dataModel?.attribute(rootName) ?? null : null;
            }
            if (!rootAttribute) {
                logger.error(`Main attribute ${attributeName} used in validation message was not found`, validation);
                return validation.message;
            }

            const secondValidationPathEntry = validation.path[1];
            const containerFromPath = getContainerFromPath(validation.path);

            // Use container from path when we have a row index (table/collection/multi-dim/group entry)
            if (containerFromPath) {
                const containerAttribute = dataModel?.attribute(containerFromPath.containerName);
                validation.params.err_row = containerFromPath.rowIndex + 1;

                if (containerAttribute?.typeName === DataModelAttributeTypeName.MultiDimensional) {
                    prefixKey += '.MULTI_DIMENSIONAL';
                } else if (containerAttribute?.typeName === DataModelAttributeTypeName.Collection) {
                    prefixKey += '.COLLECTION';
                } else if (containerAttribute?.typeName === DataModelAttributeTypeName.EnumSet) {
                    prefixKey += '.ENUM_SET';
                } else if (containerAttribute?.typeName === DataModelAttributeTypeName.Group) {
                    prefixKey += '.GROUP';
                }
                // else: no suffix, use base prefix with err_row
            } else if (rootAttribute.typeName === DataModelAttributeTypeName.MultiDimensional) {
                prefixKey += '.MULTI_DIMENSIONAL';
            } else if (rootAttribute.typeName === DataModelAttributeTypeName.Dimensional) {
                prefixKey += '.DIMENSIONAL';
            } else if (isNumber(secondValidationPathEntry)) {
                validation.params.err_row = secondValidationPathEntry + 1;

                if (rootAttribute.typeName === DataModelAttributeTypeName.Group) {
                    const errorIndex = validation.path[validation.path.length - 3];
                    if (isNumber(errorIndex)) {
                        validation.params.err_row = errorIndex + 1;
                    }
                } else if (rootAttribute.typeName === DataModelAttributeTypeName.Collection) {
                    prefixKey += '.COLLECTION';
                } else if (rootAttribute.typeName === DataModelAttributeTypeName.EnumSet) {
                    prefixKey += '.ENUM_SET';
                }
            }

            translatedPrefix = t(prefixKey, validation.params, 'messageformat');
            // When a type-specific key (e.g. .GROUP) has no translation, fall back to base prefix
            if (translatedPrefix === prefixKey && prefixKey !== 'VALIDATION_MESSAGE_LOCATION_PREFIX') {
                const basePrefix = t('VALIDATION_MESSAGE_LOCATION_PREFIX', validation.params, 'messageformat');
                if (basePrefix !== 'VALIDATION_MESSAGE_LOCATION_PREFIX') {
                    translatedPrefix = basePrefix;
                }
            }
        }

        const resolvedTranslation = translateMessageKey(validation.params);

        // useTranslation doesn't handle the @: syntax,
        // which is used for referencing other translation keys automatically
        if (resolvedTranslation !== null && resolvedTranslation.startsWith('@:')) {
            translatedValidationMessage = t(resolvedTranslation.substring(2), validation.params, 'messageformat');
        } else if (resolvedTranslation !== null) {
            translatedValidationMessage = resolvedTranslation;
        }

        // Prefer translated message body; only fall back to raw when the message key had no translation.
        // If only the prefix failed to translate, still show the translated message (with or without prefix).
        const prefixText =
            translatedPrefix !== prefixKey ? translatedPrefix : '';
        const messageText =
            resolvedTranslation !== null ? translatedValidationMessage : validation.message;
        if (messageText === validation.message && prefixText === '') {
            return validation.message;
        }
        return prefixText ? `${prefixText} ${messageText}` : messageText;

    //eslint-disable-next-line
    }, []);

    return {
        translateValidationLabel,
        translateValidationMessage,
    };

};

export { useValidationTranslator };
